import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Ban,
  Info,
  Lock,
  Search,
  X,
} from "lucide-react";
import { apiFetch } from "@/lib/api";
import { formatBRL } from "@/lib/format";
import {
  formatPosition,
  formatScore,
  type LiveEntry,
} from "@/lib/liveLeaderboard";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/Toast";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ScreenSpinner } from "@/components/ui/Spinner";
import type { FinanceSettings, Tournament } from "@/types";

type FieldPlayer = {
  id: string;
  name: string;
  country: string;
  worldRank: number;
  oddsWin: number | null;
};

type TournamentBetRow = {
  betId: string;
  userId: string;
  playerIds: string[];
};

// Réplica web da tela de palpite original (reference/expo-frontend/app/
// palpite.tsx): escolha de 3 jogadores do field, busca, score ao vivo por
// jogador, valor da aposta (sem valor sugerido — usuário digita) e
// confirmação em duas etapas. O débito acontece server-side no POST
// /api/bets.
//
// Coluna de rank: com o torneio em andamento (feed de leaderboard disponível)
// mostra a POSIÇÃO no torneio e ordena por ela; antes do início — a janela
// típica de apostas — cai no ranking mundial (OWGR), como antes.
export default function PalpitePage() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { showToast } = useToast();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [players, setPlayers] = useState<FieldPlayer[]>([]);
  const [leaderboard, setLeaderboard] = useState<LiveEntry[]>([]);
  const [myBetCount, setMyBetCount] = useState(0);
  const [settings, setSettings] = useState<FinanceSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [amountStr, setAmountStr] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Aviso exibido apenas no PRIMEIRO palpite do usuário (nenhuma aposta no
  // servidor): créditos da plataforma são a única forma de apostar, mas o
  // prêmio é pago em dinheiro.
  const [showFirstBetNotice, setShowFirstBetNotice] = useState(false);

  // REGRA: apostas usam exclusivamente créditos da plataforma — o saldo
  // disponível (prêmios) é sacável, nunca apostável.
  const credit = user?.creditBalance ?? 0;

  useEffect(() => {
    if (!tournamentId) return;
    Promise.all([
      apiFetch<Tournament[]>("/api/tournaments", { auth: false }),
      apiFetch<FieldPlayer[]>(
        `/api/players?tournamentId=${encodeURIComponent(tournamentId)}`,
        { auth: false },
      ),
      apiFetch<FinanceSettings>("/api/settings", { auth: false }),
      apiFetch<TournamentBetRow[]>(
        `/api/tournaments/${encodeURIComponent(tournamentId)}/bets`,
      ).catch(() => [] as TournamentBetRow[]),
      // Falhou? Assume que já apostou para não exibir o aviso indevidamente.
      apiFetch<{ bets: unknown[] }>("/api/me/bets").catch(() => ({
        bets: [null],
      })),
      // Feed ao vivo: vazio antes do torneio começar (ou em falha) — a tela
      // volta ao ranking mundial nesses casos.
      apiFetch<LiveEntry[]>(
        `/api/tournaments/${encodeURIComponent(tournamentId)}/leaderboard`,
        { auth: false },
      ).catch(() => [] as LiveEntry[]),
    ])
      .then(([tournaments, field, s, bets, myBets, lb]) => {
        setShowFirstBetNotice(myBets.bets.length === 0);
        setTournament(tournaments.find((t) => t.id === tournamentId) ?? null);
        setPlayers(field);
        setLeaderboard(lb);
        setSettings(s);
        setMyBetCount(bets.filter((b) => b.userId === user?.id).length);
      })
      .catch(() => {
        showToast({ type: "error", message: "Erro ao carregar o torneio." });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  const parsedAmount = Number.parseInt(amountStr, 10);
  const amountError = useMemo<string | null>(() => {
    if (amountStr.trim() === "") return "Digite um valor";
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0)
      return "Valor inválido";
    if (parsedAmount > credit) return "Acima dos seus créditos";
    return null;
  }, [amountStr, parsedAmount, credit]);

  const maxBets = settings?.maxBetsPerTournament ?? 0;
  const limitReached = maxBets > 0 && myBetCount >= maxBets;

  // Posição ao vivo por jogador. Posição null (CUT/WD/DQ) fica fora do map
  // e cai no final da lista, junto com quem não está no feed.
  const livePositionById = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of leaderboard) {
      if (e.position !== null) m.set(e.playerId, e.position);
    }
    return m;
  }, [leaderboard]);
  const liveMode = livePositionById.size > 0;

  // Score total por jogador (feed ao vivo). Fora do feed = "—" na coluna.
  const liveScoreById = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const e of leaderboard) m.set(e.playerId, e.totalScore ?? null);
    return m;
  }, [leaderboard]);

  // Em modo ao vivo, reordena o field pela posição no torneio (desempate:
  // ranking mundial). Sem feed, mantém a ordem do servidor (OWGR).
  const ordered = useMemo(() => {
    if (!liveMode) return players;
    return [...players].sort((a, b) => {
      const pa = livePositionById.get(a.id) ?? Infinity;
      const pb = livePositionById.get(b.id) ?? Infinity;
      if (pa !== pb) return pa - pb;
      return a.worldRank - b.worldRank;
    });
  }, [players, liveMode, livePositionById]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || p.country.toLowerCase().includes(q),
    );
  }, [query, ordered]);

  const playerById = useMemo(
    () => new Map(players.map((p) => [p.id, p])),
    [players],
  );

  const toggle = (id: string) => {
    setSelected((curr) => {
      if (curr.includes(id)) return curr.filter((x) => x !== id);
      if (curr.length >= 3) return curr;
      return [...curr, id];
    });
  };

  const isReady = selected.length === 3;
  const canPay = isReady && amountError === null && !limitReached;

  const submit = async () => {
    if (!canPay || !tournament || submitting) return;
    setSubmitting(true);
    try {
      await apiFetch("/api/bets", {
        method: "POST",
        body: JSON.stringify({
          id: `bet-${crypto.randomUUID()}`,
          tournamentId: tournament.id,
          playerIds: selected,
          playerNames: selected.map((id) => playerById.get(id)?.name ?? ""),
          amount: parsedAmount,
        }),
      });
      await refreshUser();
      showToast({ type: "success", message: "Palpite registrado! Boa sorte." });
      navigate("/meus-palpites", { replace: true });
    } catch (err) {
      showToast({
        type: "error",
        message:
          err instanceof Error ? err.message : "Não foi possível registrar.",
      });
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <ScreenSpinner />;

  if (!tournament) {
    return (
      <CenteredNotice
        icon={<Ban size={40} className="text-muted-foreground" />}
        title="Torneio não encontrado"
        onBack={() => navigate("/")}
      />
    );
  }

  if (tournament.betsEnabled === false) {
    return (
      <CenteredNotice
        icon={<Ban size={40} className="text-[#B45309]" />}
        title="Apostas desativadas"
        body={`O administrador desativou temporariamente os palpites em ${tournament.name}. Nenhum valor foi debitado do seu saldo.`}
        onBack={() => navigate("/")}
      />
    );
  }

  const bettingClosed =
    (tournament.status !== "active" && tournament.status !== "upcoming") ||
    new Date(tournament.deadline).getTime() < Date.now();

  if (bettingClosed) {
    return (
      <CenteredNotice
        icon={<Lock size={40} className="text-muted-foreground" />}
        title="Apostas encerradas"
        body={`O prazo para palpites em ${tournament.name} já expirou. Nenhum valor foi debitado do seu saldo.`}
        onBack={() => navigate("/")}
      />
    );
  }

  return (
    <div className="flex min-h-full flex-col">
      {/* Selected pills */}
      <div className="bg-surface-alt px-5 pb-4 pt-5">
        <button
          type="button"
          aria-label="Voltar"
          onClick={() => navigate(-1)}
          className="mb-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/18 bg-white/8 text-surface-alt-foreground/80 transition-colors hover:text-surface-alt-foreground"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-[22px] font-bold text-surface-alt-foreground">
          Escolha seu Top 3
        </h1>
        <p className="mb-3.5 text-[15px] text-surface-alt-foreground/65">
          Sem ordem · {tournament.name}
        </p>
        <div className="flex gap-2">
          {[0, 1, 2].map((idx) => {
            const id = selected[idx];
            const player = id ? playerById.get(id) : undefined;
            return (
              <button
                key={idx}
                onClick={() => id && toggle(id)}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-(--radius-field) border px-2.5 py-3 ${
                  player
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-white/18 bg-white/8 text-surface-alt-foreground/50"
                }`}
              >
                {player ? (
                  <>
                    <span className="truncate text-[13px] font-semibold">
                      {player.name.split(" ").slice(-1)[0]}
                    </span>
                    <X size={12} />
                  </>
                ) : (
                  <span className="text-xs font-medium">Vazio</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {limitReached && (
        <div className="mx-5 mt-4 rounded-(--radius-field) border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground">
          Limite de {maxBets} palpite{maxBets > 1 ? "s" : ""} por torneio
          atingido.
        </div>
      )}

      {/* Search */}
      <div className="px-5 pt-4">
        <div className="flex h-[46px] items-center gap-2 rounded-(--radius-field) border border-border bg-card px-3.5">
          <Search size={16} className="shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar jogador"
            className="w-full bg-transparent text-sm font-medium text-foreground outline-none placeholder:text-muted-foreground"
          />
          {query.length > 0 && (
            <button onClick={() => setQuery("")} aria-label="Limpar busca">
              <X size={16} className="text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Column header */}
      <div className="flex items-center gap-2 px-[30px] pb-1 pt-2.5 text-[10px] font-medium tracking-wide text-muted-foreground">
        <span className="w-[30px] text-center">{liveMode ? "POS" : "RANK"}</span>
        <span className="flex-1">NOME</span>
        <span className="w-[44px] text-right">PAÍS</span>
        <span className="w-[52px] text-center">SCORE</span>
      </div>

      {/* Player list */}
      <div className="flex-1 px-5 pb-44">
        {filtered.map((p) => {
          const isSelected = selected.includes(p.id);
          return (
            <button
              key={p.id}
              data-testid="player-row"
              onClick={() => toggle(p.id)}
              className={`mb-1.5 flex w-full items-center gap-2 rounded-(--radius-field) border px-2.5 py-2.5 text-left transition-colors ${
                isSelected
                  ? "border-accent bg-accent/15"
                  : "border-border bg-card hover:border-muted-foreground/40"
              }`}
            >
              <span className="w-[30px] shrink-0 text-center text-xs font-semibold text-muted-foreground">
                {liveMode
                  ? livePositionById.has(p.id)
                    ? formatPosition(livePositionById.get(p.id)!)
                    : "—"
                  : p.worldRank < 9999
                    ? p.worldRank
                    : "—"}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                {p.name}
              </span>
              <span className="w-[44px] shrink-0 truncate text-right text-xs text-muted-foreground">
                {p.country}
              </span>
              <span className="w-[52px] shrink-0 text-center text-xs font-medium text-muted-foreground">
                {liveScoreById.has(p.id)
                  ? formatScore(liveScoreById.get(p.id))
                  : "—"}
              </span>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nenhum jogador encontrado.
          </p>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="fixed inset-x-0 bottom-16 z-10 border-t border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-lg items-center gap-3 px-5 py-3.5 pb-[calc(0.875rem+env(safe-area-inset-bottom))]">
          <div className="flex-1">
            <p className="text-[10px] font-medium tracking-[1px] text-muted-foreground">
              VALOR DA APOSTA
            </p>
            <div
              className={`mt-1 flex h-10 items-center gap-1 rounded-[10px] border bg-card px-2.5 ${
                amountError ? "border-destructive" : "border-border"
              }`}
            >
              <span className="text-sm font-semibold text-muted-foreground">
                R$
              </span>
              <input
                value={amountStr}
                onChange={(e) =>
                  setAmountStr(e.target.value.replace(/[^0-9]/g, ""))
                }
                inputMode="numeric"
                maxLength={9}
                placeholder="0"
                className="w-full bg-transparent text-lg font-bold text-foreground outline-none placeholder:text-muted-foreground"
              />
            </div>
            <p
              className={`mt-1 truncate text-[11px] font-medium ${
                amountError ? "text-destructive" : "text-muted-foreground"
              }`}
            >
              {amountError ?? `Créditos disponíveis ${formatBRL(credit)}`}
            </p>
          </div>
          <Button
            title={isReady ? "Confirmar" : `${selected.length}/3`}
            icon={isReady ? <ArrowRight size={18} /> : undefined}
            disabled={!canPay}
            onClick={() => setConfirming(true)}
            className="min-w-[140px]"
          />
        </div>
      </div>

      {/* First-bet notice */}
      {showFirstBetNotice && (
        <div
          className="fixed inset-0 z-20 flex items-end justify-center bg-black/60 sm:items-center"
          onClick={() => setShowFirstBetNotice(false)}
        >
          <Card
            className="w-full max-w-lg rounded-b-none p-5 sm:rounded-(--radius-app)"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2.5">
              <Info size={20} className="shrink-0 text-accent" />
              <h2 className="text-lg font-bold">Seu primeiro palpite</h2>
            </div>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              Para o torneio{" "}
              <span className="font-semibold text-foreground">
                {tournament.name}
              </span>
              , você poderá fazer até {maxBets > 0 ? maxBets : 3} palpite
              {(maxBets > 0 ? maxBets : 3) > 1 ? "s" : ""} usando os créditos
              liberados pela Plataforma. Mas, se acertar, seu prêmio será em
              dinheiro.
            </p>
            <Button
              title="Entendi"
              onClick={() => setShowFirstBetNotice(false)}
              className="mt-5 w-full"
            />
          </Card>
        </div>
      )}

      {/* Confirm dialog */}
      {confirming && (
        <div
          className="fixed inset-0 z-20 flex items-end justify-center bg-black/60 sm:items-center"
          onClick={() => !submitting && setConfirming(false)}
        >
          <Card
            className="w-full max-w-lg rounded-b-none p-5 sm:rounded-(--radius-app)"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold">Confirmar palpite</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {tournament.name}
            </p>
            <div className="mt-4 flex flex-col gap-2">
              {selected.map((id) => (
                <div
                  key={id}
                  className="rounded-(--radius-field) bg-surface-alt px-4 py-2.5 text-sm font-semibold"
                >
                  {playerById.get(id)?.name ?? id}
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between rounded-(--radius-field) border border-border px-4 py-3">
              <span className="text-sm text-muted-foreground">Valor</span>
              <span className="text-base font-bold">
                {formatBRL(parsedAmount)}
              </span>
            </div>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              Créditos após o palpite: {formatBRL(credit - parsedAmount)}
            </p>
            <div className="mt-4 flex gap-3">
              <Button
                title="Voltar"
                variant="ghost"
                disabled={submitting}
                onClick={() => setConfirming(false)}
                className="flex-1"
              />
              <Button
                title="Confirmar palpite"
                loading={submitting}
                onClick={() => void submit()}
                className="flex-1"
              />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function CenteredNotice({
  icon,
  title,
  body,
  onBack,
}: {
  icon: React.ReactNode;
  title: string;
  body?: string;
  onBack: () => void;
}) {
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center gap-3 px-6 text-center">
      {icon}
      <h1 className="text-[22px] font-bold">{title}</h1>
      {body && (
        <p className="text-sm leading-5 text-muted-foreground">{body}</p>
      )}
      <Button
        title="Voltar ao início"
        icon={<ArrowLeft size={18} />}
        onClick={onBack}
        className="mt-3"
      />
    </div>
  );
}
